import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Form, Formik, FormikHelpers } from "formik";
import * as yup from "yup";
import { AuthCard } from "./components/constants";
import { BasicPage, FieldText, FormButton } from "./components";
import { useBoundStore } from "../../store";
import { portalBasePath } from "../../utils/portalPath";
import { rememberSignInMethod } from "../../utils/lastSignInMethod";
import {
    GoogleReturnContext,
    GoogleSession,
    GoogleSignedIn,
    fetchGoogleSession,
    googleErrorCode,
    googleLink,
    googleLogin,
    googleMessage,
    safeReturnPath,
    takeGoogleContext
} from "../../components/auth/googleSignIn";

// Where Google sends the browser back (via the API) after "Continue with
// Google". Reads the API's session and finishes: sign in, link with the
// account's password, or go back to the registration form in Google mode.
// The verified identity never appears here; only its email, for display.

type View =
    | { kind: "loading" }
    | { kind: "link"; session: GoogleSession }
    | { kind: "message"; text: string; registerLinks?: boolean; backTo?: string };

const linkSchema = yup.object().shape({
    password: yup.string().required("Enter your password")
});

const loginPathFor = (ctx: GoogleReturnContext | null) =>
    ctx?.rmnSlug ? `${portalBasePath(ctx.rmnSlug)}/login` : "/login";

// A failed sign-up offers the form it started on, query and all, so a
// checkout or funnel link isn't lost.
const failureView = (ctx: GoogleReturnContext | null, text: string): View => {
    if (ctx?.intent !== "brand" && ctx?.intent !== "expert") return { kind: "message", text };
    const back = safeReturnPath(ctx.returnPath);
    const fallback = ctx.intent === "expert" ? "/experts/apply" : "/register";
    return { kind: "message", text, backTo: back !== "/" ? back : fallback };
};

export function GoogleReturnPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const autoLogin = useBoundStore((state) => state.autoLogin);
    const [view, setView] = useState<View>({ kind: "loading" });
    const ctxRef = useRef<GoogleReturnContext | null>(null);
    const ran = useRef(false);

    const finishSignIn = async (signedIn: GoogleSignedIn) => {
        await autoLogin({ tokens: signedIn.tokens });
        rememberSignInMethod("google");
        const ctx = ctxRef.current;
        let target = "/";
        if (ctx?.intent === "login") {
            // The login pages honour ?redirect=; the branded one otherwise
            // lands on its own portal.
            const redirect = new URLSearchParams(safeReturnPath(ctx.returnPath).split("?")[1] || "").get("redirect");
            if (redirect) target = safeReturnPath(redirect);
            else if (ctx.rmnSlug) target = portalBasePath(ctx.rmnSlug);
        }
        navigate(target, { replace: true });
    };

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;
        const ctx = takeGoogleContext();
        ctxRef.current = ctx;

        // A Connect from Settings that fails or is cancelled goes back to
        // Settings with the message: the person is signed in, so a
        // "Log in" page would strand them.
        const fail = (text: string) => {
            if (ctx?.intent === "connect") {
                const back = safeReturnPath(ctx.returnPath);
                navigate(back !== "/" ? back : "/settings/sign-in-methods", {
                    replace: true,
                    state: { googleConnectError: text }
                });
                return;
            }
            setView(failureView(ctx, text));
        };

        const error = searchParams.get("error");
        if (error) {
            fail(googleMessage(error));
            return;
        }

        const run = async () => {
            try {
                const session = await fetchGoogleSession();
                switch (session.next) {
                    case "login": {
                        const signedIn = await googleLogin(session.flowId);
                        await finishSignIn(signedIn);
                        break;
                    }
                    case "link":
                        setView({ kind: "link", session });
                        break;
                    case "register": {
                        // Back to the form the person started on, in Google mode.
                        const base = ctx && ctx.intent !== "login" ? safeReturnPath(ctx.returnPath) : null;
                        const fallback = session.intent === "expert" ? "/experts/apply" : "/register";
                        navigate(base || fallback, {
                            replace: true,
                            state: {
                                google: {
                                    flowId: session.flowId,
                                    email: session.email,
                                    firstName: session.firstName,
                                    lastName: session.lastName
                                }
                            }
                        });
                        break;
                    }
                    case "connect": {
                        // Settings › Sign-in methods finishes it: that page is
                        // signed in, so the API gets the user's token there
                        // (this public page wouldn't send an expired one).
                        const back = ctx && ctx.intent === "connect" ? safeReturnPath(ctx.returnPath) : null;
                        navigate(back && back !== "/" ? back : "/settings/sign-in-methods", {
                            replace: true,
                            state: { googleConnect: { flowId: session.flowId, email: session.email } }
                        });
                        break;
                    }
                    case "no_account":
                        if (ctx?.rmnSlug) {
                            setView({
                                kind: "message",
                                text: "No account uses this Google email. Ask your network admin for an invite."
                            });
                        } else {
                            setView({
                                kind: "message",
                                text: `No account uses ${session.email}. Register below, or log in with your email and password.`,
                                registerLinks: true
                            });
                        }
                        break;
                    case "refused":
                    default:
                        fail(googleMessage(session.code));
                }
            } catch (e) {
                fail(googleMessage(googleErrorCode(e)));
            }
        };
        run();
        // Runs once on mount: the flow comes from the API's session cookie.
    }, []); // eslint-disable-line

    const handleLink = async (values: { password: string }, helpers: FormikHelpers<{ password: string }>) => {
        if (view.kind !== "link") return;
        helpers.setSubmitting(true);
        try {
            const signedIn = await googleLink(view.session.flowId, values.password);
            await finishSignIn(signedIn);
        } catch (e) {
            const code = googleErrorCode(e);
            if (code === "LoginCredentialsIncorrect") {
                helpers.setErrors({ password: googleMessage(code) });
            } else {
                setView(failureView(ctxRef.current, googleMessage(code)));
            }
        }
        helpers.setSubmitting(false);
    };

    const loginPath = loginPathFor(ctxRef.current);

    return (
        <BasicPage>
            <AuthCard elevation={0} variant="outlined">
                <Box textAlign="center" display="flex" flexDirection="column" gap={4}>
                    {view.kind === "loading" && (
                        <>
                            <Typography variant="h5">Signing you in</Typography>
                            <CircularProgress sx={{ alignSelf: "center" }} />
                        </>
                    )}

                    {view.kind === "link" && (
                        <>
                            <Typography variant="h5">Connect Google</Typography>
                            <Typography variant="body2">
                                An account already uses <b>{view.session.email}</b>. Enter its password once to connect
                                Google; after that you can sign in with either.
                            </Typography>
                            {view.session.hasPassword === false ? (
                                <Typography variant="body2">
                                    {googleMessage("NoPasswordSet")}{" "}
                                    <Link to="/forgot-password" style={{ textDecoration: "underline" }}>
                                        Forgot password
                                    </Link>
                                </Typography>
                            ) : (
                                <Formik
                                    initialValues={{ password: "" }}
                                    validationSchema={linkSchema}
                                    validateOnChange={false}
                                    onSubmit={handleLink}
                                >
                                    {({ isSubmitting }) => (
                                        <Form>
                                            <Stack spacing={3}>
                                                <FieldText
                                                    id="password"
                                                    name="password"
                                                    type="password"
                                                    placeholder="Password"
                                                    fullWidth
                                                />
                                                <FormButton text="Connect and log in" isLoadingBtn loading={isSubmitting} />
                                                <Link to="/forgot-password">
                                                    <Typography variant="body2" className="text-text-2">
                                                        Forgot Password
                                                    </Typography>
                                                </Link>
                                            </Stack>
                                        </Form>
                                    )}
                                </Formik>
                            )}
                        </>
                    )}

                    {view.kind === "message" && (
                        <>
                            <Typography variant="h5">Google sign-in</Typography>
                            <Typography variant="body2">{view.text}</Typography>
                            <Stack direction="row" spacing={3} justifyContent="center">
                                {view.backTo && (
                                    <Link to={view.backTo}>
                                        <Typography variant="body2" className="text-text-2">
                                            Back to registration
                                        </Typography>
                                    </Link>
                                )}
                                <Link to={loginPath}>
                                    <Typography variant="body2" className="text-text-2">
                                        Log in
                                    </Typography>
                                </Link>
                                {view.registerLinks && (
                                    <>
                                        <Link to="/register">
                                            <Typography variant="body2" className="text-text-2">
                                                Brand Registration
                                            </Typography>
                                        </Link>
                                        <Link to="/experts/apply">
                                            <Typography variant="body2" className="text-text-2">
                                                Expert Registration
                                            </Typography>
                                        </Link>
                                    </>
                                )}
                            </Stack>
                        </>
                    )}
                </Box>
            </AuthCard>
        </BasicPage>
    );
}
